import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { insetSx } from "../center/ownerCenterUi";
import { minutesLabel } from "./ownerProjectUi";

let draftKey = 0;
function emptyDraft() {
  draftKey += 1;
  return { key: `step-${draftKey}`, title: "", estimatedMinutes: "" };
}

function stepState(step) {
  if (step.status === "done") return { label: "Hecho", color: "success" };
  const code = String(step?.executability?.code || "");
  if (code.startsWith("dependency_")) return { label: "Depende", color: "warning" };
  const state = String(step?.work?.state || "ready");
  if (state === "waiting") return { label: "Esperando", color: "warning" };
  if (state === "blocked") return { label: "Trabado", color: "error" };
  if (state === "in_progress") return { label: "Haciendo", color: "primary" };
  return { label: "Listo", color: "default" };
}

function StepRow({ step, index, pendingNumber, total, moving, deleting, onMove, onEdit, onDelete }) {
  const state = stepState(step);
  return <Box sx={{ ...insetSx, p: .95 }}>
    <Stack direction="row" spacing={.8} alignItems="center">
      <Box sx={{ width: 25, height: 25, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.hover", flexShrink: 0 }}>
        {step.status === "done" ? <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} /> : <Typography sx={{ fontWeight: 1000, fontSize: ".68rem" }}>{pendingNumber}</Typography>}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography sx={{ fontWeight: 950, fontSize: ".83rem", textDecoration: step.status === "done" ? "line-through" : "none", opacity: step.status === "done" ? .72 : 1 }}>{step.title}</Typography>
          <Chip size="small" label={state.label} color={state.color} variant={state.label === "Listo" ? "outlined" : "filled"} sx={{ height: 19, fontSize: ".58rem", fontWeight: 900 }} />
          {step.estimatedMinutes ? <Chip size="small" variant="outlined" label={`≈ ${minutesLabel(step.estimatedMinutes)}`} sx={{ height: 19, fontSize: ".58rem" }} /> : null}
        </Stack>
        {step?.executability?.code?.startsWith?.("dependency_") && step?.executability?.dependency?.title ? <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".67rem" }}>No disponible: primero falta {step.executability.dependency.title}</Typography> : null}
        {step?.work?.state === "waiting" && step?.work?.waitingFor ? <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".67rem" }}>Esperando: {step.work.waitingFor}</Typography> : null}
        {step?.work?.state === "blocked" && (step?.work?.blockedReason || step?.work?.blockedBy) ? <Typography sx={{ mt: .2, color: step.status === "done" ? "text.secondary" : "error.main", fontSize: ".67rem" }}>{step.status === "done" ? "Traba resuelta: " : "Traba: "}{step.work.blockedReason || step.work.blockedBy}</Typography> : null}
        {step?.work?.state === "blocked" && step?.work?.unblockAction ? <Typography sx={{ mt: .15, color: "text.secondary", fontSize: ".65rem" }}>Para destrabar: {step.work.unblockAction}</Typography> : null}
      </Box>
      <Stack direction="row" spacing={.05} sx={{ flexShrink: 0 }}>
        <Tooltip title="Subir"><span><IconButton size="small" disabled={moving || index === 0} onClick={() => onMove(index, -1)}><KeyboardArrowUpRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Bajar"><span><IconButton size="small" disabled={moving || index >= total - 1} onClick={() => onMove(index, 1)}><KeyboardArrowDownRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
        {step.status === "open" ? <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit?.(step)}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip> : null}
        {step.status === "open" ? <Tooltip title="Sacar del plan"><span><IconButton size="small" disabled={deleting} onClick={() => onDelete?.(step, pendingNumber)}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip> : null}
      </Stack>
    </Stack>
  </Box>;
}

export default function OwnerProjectStepsPanel({ steps, onCreateSteps, onReorderSteps, onDeleteStep, onEdit }) {
  const items = steps?.items || [];
  const [adding, setAdding] = React.useState(false);
  const [drafts, setDrafts] = React.useState(() => [emptyDraft(), emptyDraft()]);
  const [saving, setSaving] = React.useState(false);
  const [moving, setMoving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState("");
  const [error, setError] = React.useState("");

  const updateDraft = (key, field, value) => setDrafts((prev) => prev.map((row) => row.key === key ? { ...row, [field]: value } : row));
  const addDraft = () => setDrafts((prev) => [...prev, emptyDraft()]);
  const removeDraft = (key) => setDrafts((prev) => prev.length <= 1 ? prev : prev.filter((row) => row.key !== key));

  const saveDrafts = async () => {
    const rows = drafts
      .map((row) => ({ title: String(row.title || "").trim(), estimatedMinutes: row.estimatedMinutes === "" ? null : Number(row.estimatedMinutes) }))
      .filter((row) => row.title);
    if (!rows.length) { setError("Escribí al menos un paso."); return; }
    const invalidDuration = rows.find((row) => row.estimatedMinutes !== null && (!Number.isFinite(row.estimatedMinutes) || row.estimatedMinutes < 5 || row.estimatedMinutes > 1440));
    if (invalidDuration) { setError("Las duraciones deben estar entre 5 minutos y 24 horas."); return; }
    setSaving(true); setError("");
    try {
      await onCreateSteps?.(rows);
      setDrafts([emptyDraft(), emptyDraft()]);
      setAdding(false);
    } catch (err) {
      setError(err?.message || "No pude agregar los pasos.");
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, delta) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= items.length || moving) return;
    const ordered = items.map((row) => row.id);
    [ordered[index], ordered[nextIndex]] = [ordered[nextIndex], ordered[index]];
    setMoving(true); setError("");
    try {
      await onReorderSteps?.(ordered);
    } catch (err) {
      setError(err?.message || "No pude guardar el nuevo orden.");
    } finally {
      setMoving(false);
    }
  };

  const deleteStep = async (step, number) => {
    if (!step?.id || deletingId) return;
    const confirmed = window.confirm(`¿Sacar el paso ${number} del plan activo?\n\n${step.title}\n\nSe conserva el historial; no se borra físicamente.`);
    if (!confirmed) return;
    setDeletingId(step.id); setError("");
    try {
      await onDeleteStep?.(step);
    } catch (err) {
      setError(err?.message || "No pude sacar ese paso del plan.");
    } finally {
      setDeletingId("");
    }
  };

  const doneCount = Number(steps?.doneCount ?? items.filter((row) => row.status === "done").length);
  const openCount = Number(steps?.openCount ?? items.filter((row) => row.status === "open").length);
  const pendingNumberById = new Map();
  let pendingNumber = 0;
  items.forEach((row) => {
    if (row.status !== "open") return;
    pendingNumber += 1;
    pendingNumberById.set(row.id, pendingNumber);
  });

  return <Box data-owner-jarvis-section="project-steps" sx={{ mt: 1.05 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Box>
        <Typography sx={{ fontWeight: 1000, fontSize: ".82rem" }}>Pasos del proyecto</Typography>
        <Typography sx={{ mt: .08, color: "text.secondary", fontSize: ".68rem" }}>{items.length ? `${doneCount} hechos · ${openCount} por resolver` : "Desarmá el objetivo en acciones concretas."}</Typography>
      </Box>
      <Button size="small" variant={adding ? "text" : "outlined"} startIcon={<PlaylistAddRoundedIcon />} onClick={() => { setAdding((value) => !value); setError(""); }} sx={{ textTransform: "none", fontWeight: 900 }}>{adding ? "Cerrar" : "Agregar varios"}</Button>
    </Stack>

    <Collapse in={adding} unmountOnExit>
      <Box sx={{ mt: .8, ...insetSx, p: 1 }}>
        <Typography sx={{ fontWeight: 950, fontSize: ".75rem" }}>Cargá varios sin abrir un formulario por cada uno</Typography>
        <Typography sx={{ mt: .15, mb: .8, color: "text.secondary", fontSize: ".67rem" }}>El título alcanza. La duración es opcional y siempre corresponde a esa acción, no al proyecto completo.</Typography>
        <Stack spacing={.65}>
          {drafts.map((row, index) => <Stack key={row.key} direction={{ xs: "column", sm: "row" }} spacing={.55} alignItems={{ sm: "center" }}>
            <TextField size="small" fullWidth label={`Paso ${index + 1}`} value={row.title} onChange={(event) => updateDraft(row.key, "title", event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && index === drafts.length - 1) { event.preventDefault(); addDraft(); } }} />
            <TextField size="small" type="number" label="Min" value={row.estimatedMinutes} onChange={(event) => updateDraft(row.key, "estimatedMinutes", event.target.value)} inputProps={{ min: 5, max: 1440, step: 5 }} sx={{ width: { xs: "100%", sm: 105 }, flexShrink: 0 }} />
            <IconButton size="small" disabled={drafts.length <= 1} onClick={() => removeDraft(row.key)} sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Stack>)}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.7} sx={{ mt: .8 }}>
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={addDraft} sx={{ textTransform: "none", fontWeight: 900 }}>Otro paso</Button>
          <Button size="small" variant="contained" disabled={saving} onClick={saveDrafts} sx={{ textTransform: "none", fontWeight: 950 }}>{saving ? "Guardando…" : "Guardar pasos"}</Button>
        </Stack>
      </Box>
    </Collapse>

    {error ? <Alert severity="error" sx={{ mt: .7, py: 0 }}>{error}</Alert> : null}
    {items.length ? <Stack spacing={.45} sx={{ mt: .8 }}>{items.map((step, index) => <StepRow key={step.id} step={step} index={index} pendingNumber={pendingNumberById.get(step.id) || null} total={items.length} moving={moving} deleting={deletingId === step.id} onMove={move} onEdit={onEdit} onDelete={deleteStep} />)}</Stack> : null}
  </Box>;
}
